<template>
  <SectionContentProductShowcaseCard v-bind="props" />
</template>

<script setup>
const props = defineProps({
  headerIconSrc: {
    type: String,
    default: '/assets/images/cognition-showcase-header-icon.svg'
  },
  headerIconAlt: {
    type: String,
    default: 'Cognition Coffee icon'
  },
  productTitle: {
    type: String,
    default: 'Cognition Mushroom Coffee'
  },
  productSubtitle: {
    type: String,
    default: 'The Only Low-Acid Mushroom Coffee'
  },
  shopButtonText: {
    type: String,
    default: 'Shop Now'
  },
  shopButtonHref: {
    type: String,
    default: 'https://lifeboostcoffee.com/products/cognition-mushroom-coffee'
  },
  introContent: {
    type: String,
    default: `<p><a href="https://lifeboostcoffee.com/products/cognition-mushroom-coffee" target="_blank" rel="noopener noreferrer"><strong>Cognition Coffee</strong></a> is our mushroom-infused coffee that doesn't taste like mushroom coffee but still delivers all the benefits of such an infusion.</p>
<p>Here, we begin with our premium, low acid, stomach-friendly, non-GMO, 3rd party tested, sun dried, and spring water washed Central American coffee, these clean, pure beans then infused with lion's mane and chaga mushroom extracts.</p>
<p>As you likely know, these mushrooms are renowned for their potential cognitive-enhancing properties, and here, in Lifeboost Cognition Coffee, we've used extracts 8 times more potent than other mushroom coffees, making this the only low-acid mushroom coffee on the market with unparalleled strength.</p>
<p>When it comes to supporting the health and functionality of your brain, Lifeboost Cognition Coffee delivers!</p>
<p>And, if mushroom coffee isn't your thing, when sipping clean, healthy, sustainably grown, low acid, toxin-tested <a href="https://lifeboostcoffee.com/" target="_blank" rel="noopener noreferrer"><strong>Lifeboost Coffee</strong></a> of <u>any roast/flavor</u>, can still support powerful brain boosting benefits!</p>`
  },
  productImageSrc: {
    type: String,
    default: '/assets/images/cognition-showcase-product-mens.png'
  },
  productImageAlt: {
    type: String,
    default: 'Lifeboost Cognition Mushroom Coffee bag'
  },
  productImageWidth: {
    type: Number,
    default: 243
  },
  productImageHeight: {
    type: Number,
    default: 379
  },
  productImageRounded: {
    type: Boolean,
    default: false
  },
  ingredients: {
    type: Array,
    default: () => []
  },
  features: {
    type: Array,
    default: () => [
      { label: 'Single origin', iconSrc: '/assets/images/cognition-feature-single-origin.svg', iconAlt: 'Single origin' },
      { label: 'Non GMO', iconSrc: '/assets/images/cognition-feature-non-gmo.svg', iconAlt: 'Non GMO' },
      { label: '3rd party tested', iconSrc: '/assets/images/cognition-feature-3rd-party.png', iconAlt: '3rd party tested' },
      { label: 'Hand picked', iconSrc: '/assets/images/cognition-feature-hand-picked.svg', iconAlt: 'Hand picked' },
      { label: 'Sun dried', iconSrc: '/assets/images/cognition-feature-sun-dried.png', iconAlt: 'Sun dried' },
      { label: 'Spring water washed', iconSrc: '/assets/images/cognition-feature-spring-water.png', iconAlt: 'Spring water washed' }
    ]
  },
  statCards: {
    type: Array,
    default: () => [
      {
        text: 'Studies show regular coffee drinkers have a 29% lower risk of Parkinson\'s disease and a 28% decreased risk of dementia.',
        imageSrc: '/assets/images/cognition-stat-parkinson-mens.jpg',
        imageAlt: 'Person in wheelchair'
      },
      {
        text: 'Regular coffee consumption is also linked to improvements in reaction time, attention, learning, and overall cognitive function.',
        imageSrc: '/assets/images/cognition-stat-reaction-mens.jpg',
        imageAlt: 'Man with apple on head'
      },
      {
        text: 'Studies suggest regular coffee consumption has a profoundly positive effect on memory (short and long term).<sup>17</sup>',
        imageSrc: '/assets/images/cognition-stat-memory-mens.jpg',
        imageAlt: 'Digital brain illustration'
      }
    ]
  },
  paddingTop: {
    type: String,
    default: 'reduced'
  },
  paddingBottom: {
    type: String,
    default: 'default'
  }
})

defineBuilderComponent({
  friendlyName: 'Content - Showcase Cognition',
  inputs: [
    {
      name: 'headerIconSrc',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp', 'svg'],
      defaultValue: '/assets/images/cognition-showcase-header-icon.svg'
    },
    {
      name: 'headerIconAlt',
      type: 'text',
      defaultValue: 'Cognition Coffee icon'
    },
    {
      name: 'productTitle',
      type: 'richText',
      defaultValue: 'Cognition Mushroom Coffee'
    },
    {
      name: 'productSubtitle',
      type: 'text',
      defaultValue: 'The Only Low-Acid Mushroom Coffee'
    },
    {
      name: 'shopButtonText',
      type: 'text',
      defaultValue: 'Shop Now'
    },
    {
      name: 'shopButtonHref',
      type: 'text',
      defaultValue: 'https://lifeboostcoffee.com/products/cognition-mushroom-coffee'
    },
    {
      name: 'introContent',
      type: 'richText',
      defaultValue: `<p><a href="https://lifeboostcoffee.com/products/cognition-mushroom-coffee" target="_blank" rel="noopener noreferrer"><strong>Cognition Coffee</strong></a> is our mushroom-infused coffee that doesn't taste like mushroom coffee but still delivers all the benefits of such an infusion.</p>
<p>Here, we begin with our premium, low acid, stomach-friendly, non-GMO, 3rd party tested, sun dried, and spring water washed Central American coffee, these clean, pure beans then infused with lion's mane and chaga mushroom extracts.</p>
<p>As you likely know, these mushrooms are renowned for their potential cognitive-enhancing properties, and here, in Lifeboost Cognition Coffee, we've used extracts 8 times more potent than other mushroom coffees, making this the only low-acid mushroom coffee on the market with unparalleled strength.</p>
<p>When it comes to supporting the health and functionality of your brain, Lifeboost Cognition Coffee delivers!</p>
<p>And, if mushroom coffee isn't your thing, when sipping clean, healthy, sustainably grown, low acid, toxin-tested <a href="https://lifeboostcoffee.com/" target="_blank" rel="noopener noreferrer"><strong>Lifeboost Coffee</strong></a> of <u>any roast/flavor</u>, can still support powerful brain boosting benefits!</p>`
    },
    {
      name: 'productImageSrc',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
      defaultValue: '/assets/images/cognition-showcase-product-mens.png'
    },
    {
      name: 'productImageAlt',
      type: 'text',
      defaultValue: 'Lifeboost Cognition Mushroom Coffee bag'
    },
    {
      name: 'productImageRounded',
      type: 'boolean',
      defaultValue: false
    },
    {
      name: 'ingredients',
      type: 'list',
      defaultValue: [],
      subFields: [
        {
          name: 'imageSrc',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
          defaultValue: ''
        },
        {
          name: 'imageAlt',
          type: 'text',
          defaultValue: ''
        },
        {
          name: 'title',
          type: 'text',
          defaultValue: ''
        },
        {
          name: 'description',
          type: 'longText',
          defaultValue: ''
        }
      ]
    },
    {
      name: 'features',
      type: 'list',
      defaultValue: [
        { label: 'Single origin', iconSrc: '/assets/images/cognition-feature-single-origin.svg', iconAlt: 'Single origin' },
        { label: 'Non GMO', iconSrc: '/assets/images/cognition-feature-non-gmo.svg', iconAlt: 'Non GMO' },
        { label: '3rd party tested', iconSrc: '/assets/images/cognition-feature-3rd-party.png', iconAlt: '3rd party tested' },
        { label: 'Hand picked', iconSrc: '/assets/images/cognition-feature-hand-picked.svg', iconAlt: 'Hand picked' },
        { label: 'Sun dried', iconSrc: '/assets/images/cognition-feature-sun-dried.png', iconAlt: 'Sun dried' },
        { label: 'Spring water washed', iconSrc: '/assets/images/cognition-feature-spring-water.png', iconAlt: 'Spring water washed' }
      ],
      subFields: [
        {
          name: 'iconSrc',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp', 'svg'],
          defaultValue: ''
        },
        {
          name: 'iconAlt',
          type: 'text',
          defaultValue: ''
        },
        {
          name: 'label',
          type: 'text',
          defaultValue: ''
        }
      ]
    },
    {
      name: 'statCards',
      type: 'list',
      defaultValue: [
        {
          text: 'Studies show regular coffee drinkers have a 29% lower risk of Parkinson\'s disease and a 28% decreased risk of dementia.',
          imageSrc: '/assets/images/cognition-stat-parkinson-mens.jpg',
          imageAlt: 'Person in wheelchair'
        },
        {
          text: 'Regular coffee consumption is also linked to improvements in reaction time, attention, learning, and overall cognitive function.',
          imageSrc: '/assets/images/cognition-stat-reaction-mens.jpg',
          imageAlt: 'Man with apple on head'
        },
        {
          text: 'Studies suggest regular coffee consumption has a profoundly positive effect on memory (short and long term).<sup>17</sup>',
          imageSrc: '/assets/images/cognition-stat-memory-mens.jpg',
          imageAlt: 'Digital brain illustration'
        }
      ],
      subFields: [
        {
          name: 'imageSrc',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
          defaultValue: ''
        },
        {
          name: 'imageAlt',
          type: 'text',
          defaultValue: ''
        },
        {
          name: 'text',
          type: 'richText',
          defaultValue: ''
        }
      ]
    },
    {
      name: 'paddingTop',
      type: 'text',
      enum: ['default', 'reduced'],
      defaultValue: 'reduced'
    },
    {
      name: 'paddingBottom',
      type: 'text',
      enum: ['default', 'reduced'],
      defaultValue: 'default'
    }
  ]
})
</script>
